import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { ACCEPTED_IMAGE_TYPES, uploadImage } from "../cloudinaryUpload";
import {
  PreferredDaysEditor,
  normalizePreferredDays,
} from "../preferredDays";
import KitColorsEditor, { normalizeKitColors } from "./KitColorsEditor";
import { friendlyError } from "../utils/errors";

const MyTeamEditor = ({ team, onSaved, onClose }) => {
  const [teamName, setTeamName] = useState(team.team_name || "");
  const [ownerName, setOwnerName] = useState(team.owner_name || "");
  const [phone, setPhone] = useState(team.phone || "");
  const [email, setEmail] = useState(team.email || "");
  const [logoUrl, setLogoUrl] = useState(team.logo_url || null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [preferredDays, setPreferredDays] = useState([]);
  const [primaryKitColors, setPrimaryKitColors] = useState([]);
  const [secondaryKitColors, setSecondaryKitColors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const fileInputRef = useRef(null);
  const teamRef = useRef(team);
  teamRef.current = team;

  // Reset the form only when a different team is opened. The parent reloads its
  // team list after a save or an auth token refresh, which hands us a new
  // `team` object for the same team; resetting on that wiped unsaved edits and
  // hid the "Equipo actualizado" confirmation.
  useEffect(() => {
    const team = teamRef.current;
    setTeamName(team.team_name || "");
    setOwnerName(team.owner_name || "");
    setPhone(team.phone || "");
    setEmail(team.email || "");
    setLogoUrl(team.logo_url || null);
    setLogoFile(null);
    setPrimaryKitColors(normalizeKitColors(team.primary_kit_colors));
    setSecondaryKitColors(normalizeKitColors(team.secondary_kit_colors));
    setSaved(false);
    setError(null);

    let cancelled = false;
    const loadDays = async () => {
      const { data, error: daysErr } = await supabase
        .from("team_preferred_days")
        .select("day_of_week, preferred_time_type, preferred_time")
        .eq("team_id", team.id);
      if (cancelled) return;
      if (daysErr) {
        console.error(daysErr);
        setPreferredDays([]);
        return;
      }
      setPreferredDays(
        normalizePreferredDays(data).map((d) => ({
          day_of_week: d.day_of_week,
          preferred_time_type: d.preferred_time_type || "",
          preferred_time: d.preferred_time || "",
        }))
      );
    };
    loadDays();
    return () => {
      cancelled = true;
    };
  }, [team.id]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const handleDayToggle = (dayValue) => {
    setPreferredDays((prev) => {
      const existing = prev.find((d) => d.day_of_week === dayValue);
      if (existing) return prev.filter((d) => d.day_of_week !== dayValue);
      return [
        ...prev,
        { day_of_week: dayValue, preferred_time_type: "", preferred_time: "" },
      ];
    });
    setSaved(false);
  };

  const updateDayField = (dayValue, field, value) => {
    setPreferredDays((prev) =>
      prev.map((d) => {
        if (d.day_of_week !== dayValue) return d;
        const updated = { ...d, [field]: value };
        if (field === "preferred_time_type" && !value) {
          updated.preferred_time = "";
        }
        return updated;
      })
    );
    setSaved(false);
  };

  const onSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSaved(false);

    if (!teamName.trim()) {
      setError("El nombre del equipo es obligatorio.");
      return;
    }
    if (phone.trim() && phone.replace(/\D/g, "").length < 7) {
      setError("Ingresa un teléfono válido.");
      return;
    }
    if (
      email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
    ) {
      setError("Ingresa un correo válido.");
      return;
    }

    setBusy(true);
    try {
      let nextLogo = logoUrl;
      if (logoFile) {
        const uploaded = await uploadImage(logoFile, {
          purpose: "team-logo",
          teamId: team.id,
        });
        nextLogo = uploaded.url;
      }

      const { error: teamErr } = await supabase
        .from("teams")
        .update({
          team_name: teamName.trim(),
          logo_url: nextLogo,
          primary_kit_colors: normalizeKitColors(primaryKitColors),
          secondary_kit_colors: normalizeKitColors(secondaryKitColors),
        })
        .eq("id", team.id);

      if (teamErr) throw teamErr;

      const { error: contactErr } = await supabase
        .from("team_contacts")
        .upsert(
          {
            team_id: team.id,
            owner_name: ownerName.trim() || null,
            phone: phone.trim() || null,
            email: email.trim() || null,
          },
          { onConflict: "team_id" }
        );

      if (contactErr) throw contactErr;

      const { error: delErr } = await supabase
        .from("team_preferred_days")
        .delete()
        .eq("team_id", team.id);
      if (delErr) throw delErr;

      const daysPayload = normalizePreferredDays(
        preferredDays.map((d) => ({
          day_of_week: d.day_of_week,
          preferred_time_type: d.preferred_time_type || null,
          preferred_time: d.preferred_time || null,
        }))
      );

      if (daysPayload.length > 0) {
        const { error: insErr } = await supabase
          .from("team_preferred_days")
          .insert(
            daysPayload.map((d) => ({
              team_id: team.id,
              day_of_week: d.day_of_week,
              preferred_time_type: d.preferred_time_type,
              preferred_time: d.preferred_time,
            }))
          );
        if (insErr) throw insErr;
      }

      setLogoUrl(nextLogo);
      setLogoFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setSaved(true);
      onSaved?.();
    } catch (err) {
      console.error(err);
      setError(friendlyError(err, "No se pudo guardar. Inténtalo de nuevo."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="reg-team-editor acct-editor">
      <form className="reg-form" onSubmit={onSave} noValidate>
        <fieldset className="acct-fieldset">
          <legend className="acct-legend">
            Datos públicos
            <span className="acct-legend-note">Aparecen en la tabla y los partidos.</span>
          </legend>

          <label className="reg-field">
            <span className="reg-label">
              Nombre del equipo <span className="reg-req">*</span>
            </span>
            <input
              type="text"
              value={teamName}
              onChange={(e) => {
                setTeamName(e.target.value);
                setSaved(false);
              }}
              disabled={busy}
              maxLength={80}
              required
            />
          </label>

          <div className="reg-field">
            <span className="reg-label">Logo</span>
            <div className="acct-logo-row">
              {logoPreview || logoUrl ? (
                <img src={logoPreview || logoUrl} alt="" className="acct-logo-preview" />
              ) : (
                <span className="acct-logo-preview acct-logo-empty" aria-hidden="true">
                  {(teamName || "?").charAt(0).toUpperCase()}
                </span>
              )}
              <div className="acct-logo-actions">
                <label className={`reg-btn-secondary acct-upload${busy ? " is-disabled" : ""}`}>
                  {logoPreview || logoUrl ? "Cambiar logo" : "Subir logo"}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES}
                    className="sr-only"
                    disabled={busy}
                    onChange={(e) => {
                      setLogoFile(e.target.files?.[0] || null);
                      setSaved(false);
                    }}
                  />
                </label>
                <span className="reg-hint">
                  {logoFile ? logoFile.name : "PNG, JPG o WebP, cuadrado de preferencia."}
                </span>
              </div>
            </div>
          </div>

          <div className="reg-field">
            <KitColorsEditor
              label="Uniforme principal"
              colors={primaryKitColors}
              onChange={(colors) => {
                setPrimaryKitColors(colors);
                setSaved(false);
              }}
              helpText="Opcional. Hasta 3 colores."
              disabled={busy}
            />
          </div>

          <div className="reg-field">
            <KitColorsEditor
              label="Uniforme alterno"
              colors={secondaryKitColors}
              onChange={(colors) => {
                setSecondaryKitColors(colors);
                setSaved(false);
              }}
              disabled={busy}
            />
          </div>
        </fieldset>

        <fieldset className="acct-fieldset">
          <legend className="acct-legend">
            Contacto del equipo
            <span className="acct-legend-note">Privado. Solo lo ve la administración de la liga.</span>
          </legend>

          <label className="reg-field">
            <span className="reg-label">Responsable</span>
            <input
              type="text"
              value={ownerName}
              onChange={(e) => {
                setOwnerName(e.target.value);
                setSaved(false);
              }}
              autoComplete="name"
              disabled={busy}
              maxLength={80}
            />
          </label>

          <div className="acct-field-pair">
            <label className="reg-field">
              <span className="reg-label">Teléfono</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setSaved(false);
                }}
                autoComplete="tel"
                inputMode="tel"
                disabled={busy}
                maxLength={30}
              />
            </label>

            <label className="reg-field">
              <span className="reg-label">Correo</span>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setSaved(false);
                }}
                autoComplete="email"
                inputMode="email"
                spellCheck={false}
                disabled={busy}
                maxLength={120}
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="acct-fieldset">
          <legend className="acct-legend">
            Horario
            <span className="acct-legend-note">Ayuda a la liga a programar tus partidos.</span>
          </legend>
          <PreferredDaysEditor
            preferredDays={preferredDays}
            onToggleDay={handleDayToggle}
            onUpdateDay={updateDayField}
            onClear={() => {
              setPreferredDays([]);
              setSaved(false);
            }}
            disabled={busy}
          />
        </fieldset>

        <div className="acct-editor-footer">
          <div aria-live="polite" className="acct-editor-status">
            {error && (
              <div className="reg-status error" role="alert">
                {error}
              </div>
            )}
            {saved && (
              <div className="reg-success" role="status">
                Equipo actualizado.
              </div>
            )}
          </div>
          <div className="acct-editor-actions">
            <button type="button" className="reg-btn-secondary" onClick={onClose} disabled={busy}>
              Cerrar
            </button>
            <button type="submit" className="reg-submit" disabled={busy}>
              {busy ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MyTeamEditor;
